import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";

import { fetchMarketWatchFilterOptions, fetchFeasibilityWeights } from "../../../shared/apiService";
import {
  loadMarketWatchFilters,
  clearMarketWatchFilters,
  loadFiltersFromActiveMWSheet,
} from "../../../shared/marketWatchFilterState";
import {
  executeMarketWatchPipeline,
  rerunMarketWatchAnalysis,
  MW_STALE_ANALYSIS_EVENT,
  MW_STALE_ANALYSIS_STORAGE_KEY,
} from "../../../shared/marketWatchPipeline";
import type { MarketWatchFilterState } from "../../../shared/marketWatchFilterState";
import { STORAGE_KEYS, PRICE_CLUSTER_MODES } from "../../../shared/constants";

// Import from Market-Watch module
import { MarketWatchFilterView, MARKET_WATCH_SHEET } from "../Market-Watch";
import ProgressSheet from "../common/ProgressSheet";
import MarketWatchRefreshConfirm from "../Market-Watch/MarketWatchRefreshConfirm";
import { Snackbar, Alert } from "@mui/material";

/**
 * Check whether any visible Market Watch sheets (prefix "MW ") exist.
 * Also cleans up orphaned hidden _MMData_ sheets when none remain.
 */
async function anyMarketWatchSheetsExist(): Promise<boolean> {
  let hasVisibleMW = false;
  try {
    await Excel.run(async (context) => {
      const worksheets = context.workbook.worksheets;
      worksheets.load("items/name");
      await context.sync();

      const hiddenMMDataSheets: Excel.Worksheet[] = [];
      for (const sheet of worksheets.items) {
        if (sheet.name.startsWith("MW ")) hasVisibleMW = true;
        else if (sheet.name.startsWith("_MMData_")) hiddenMMDataSheets.push(sheet);
      }

      // Clean up orphaned hidden data sheets when no visible MW sheets remain
      if (!hasVisibleMW && hiddenMMDataSheets.length > 0) {
        for (const s of hiddenMMDataSheets) s.delete();
        await context.sync();
      }
    });
  } catch (err) {
    console.warn("Could not check for Market Watch sheets:", err);
    hasVisibleMW = true; // err on the side of caution
  }
  return hasVisibleMW;
}

/** Shallow set equality — order-insensitive comparison of two string arrays */
function setsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  return b.every((v) => setA.has(v));
}

const MarketWatchRoute: React.FC = () => {
  const history = useHistory();
  const [brand, setBrand] = useState<string[]>([]);
  const [asin, setAsin] = useState<string[]>([]);
  const [keyword, setKeyword] = useState<string[]>([]);
  const [excludedBrands, setExcludedBrands] = useState<string[]>([]);
  const [isExcludeSameBrand, setIsExcludeSameBrand] = useState(false);
  const [isExcludeSpreetailAsin, setIsExcludeSpreetailAsin] = useState(false);
  const [isEnabledSeasonality, setIsEnabledSeasonality] = useState(false);
  const [priceClusterMode, setPriceClusterMode] = useState<"global" | "dynamic">(PRICE_CLUSTER_MODES.GLOBAL);
  const [priceVariancePct, setPriceVariancePct] = useState<number>(10);
  const [varianceGrowthFactor, setVarianceGrowthFactor] = useState<number | null>(null);
  const [clusterSettingsLoading, setClusterSettingsLoading] = useState(false);
  const configLoadedRef = useRef(false);
  const sheetRestoredRef = useRef(false);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [asinOptions, setAsinOptions] = useState<string[]>([]);
  const [keywordOptions, setKeywordOptions] = useState<string[]>([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [asinLoading, setAsinLoading] = useState(false);
  const [keywordLoading, setKeywordLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toast notification for cascading filter resets
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const showFilterResetToast = useCallback((message: string) => {
    setToastMessage(message);
    setToastOpen(true);
  }, []);

  // ── Cascading change handlers: detect changes via refs (always current) ──
  const handleBrandChange = useCallback(
    (newBrand: string[]) => {
      const oldBrand = brandRef.current;
      brandRef.current = newBrand;
      setBrand(newBrand);

      if (!setsEqual(newBrand, oldBrand)) {
        // Always invalidate downstream fingerprints so next open re-fetches
        optionsFingerprintRef.current.target_asin = null;
        optionsFingerprintRef.current.keyword = null;

        // Always clear cached options so next open fetches fresh
        setAsinOptions([]);
        setKeywordOptions([]);

        const currentAsins = [...asinIntentRef.current];
        const currentKeywords = [...keywordIntentRef.current];

        // If no downstream selections, nothing to cascade
        if (currentAsins.length === 0 && currentKeywords.length === 0) return;

        // Fire async cascade check (keeps callback synchronous for prop compatibility)
        void (async () => {
          const clearedParts: string[] = [];

          // ── Smart ASIN cascade: keep ASINs still valid under new brand ──
          if (currentAsins.length > 0 && newBrand.length > 0) {
            try {
              const result = await fetchMarketWatchFilterOptions("target_asin", {
                brand: newBrand,
              });
              // Staleness guard: skip if brand changed again while fetching
              if (!setsEqual(brandRef.current, newBrand)) return;

              const validAsins = new Set(result.values.map((v) => v.toLowerCase()));
              const surviving = currentAsins.filter((a) => validAsins.has(a.toLowerCase()));

              const previousActive = [...asinRef.current];

              if (!setsEqual(surviving, previousActive)) {
                asinRef.current = surviving;
                setAsin(surviving);
                const restoredCount = surviving.filter(
                  (a) => !previousActive.some((p) => p.toLowerCase() === a.toLowerCase())
                ).length;
                if (surviving.length === 0) {
                  clearedParts.push("ASIN");
                } else if (restoredCount > 0) {
                  clearedParts.push(`ASIN (${restoredCount} restored)`);
                } else {
                  clearedParts.push(`ASIN (${currentAsins.length - surviving.length} removed)`);
                }
              }
            } catch {
              // On fetch error, fall back to clearing all ASINs (safe default)
              if (!setsEqual(brandRef.current, newBrand)) return;
              asinRef.current = [];
              setAsin([]);
              clearedParts.push("ASIN");
            }
          }
          // If brand cleared entirely → ASINs unconstrained, keep them

          // ── Smart Keyword cascade: keep keywords valid under new brand + surviving ASINs ──
          if (currentKeywords.length > 0) {
            const survivingAsins = asinRef.current;
            if (newBrand.length > 0 || survivingAsins.length > 0) {
              try {
                const result = await fetchMarketWatchFilterOptions("keyword", {
                  brand: newBrand.length > 0 ? newBrand : undefined,
                  target_asin: survivingAsins.length > 0 ? survivingAsins : undefined,
                });
                // Staleness guard
                if (!setsEqual(brandRef.current, newBrand)) return;

                const validKeywords = new Set(result.values.map((v) => v.toLowerCase()));
                const survivingKw = currentKeywords.filter((k) =>
                  validKeywords.has(k.toLowerCase())
                );

                const previousActiveKw = [...keywordRef.current];

                if (!setsEqual(survivingKw, previousActiveKw)) {
                  keywordRef.current = survivingKw;
                  setKeyword(survivingKw);
                  const restoredKwCount = survivingKw.filter(
                    (k) => !previousActiveKw.some((p) => p.toLowerCase() === k.toLowerCase())
                  ).length;
                  if (survivingKw.length === 0) {
                    clearedParts.push("Keyword");
                  } else if (restoredKwCount > 0) {
                    clearedParts.push(`Keyword (${restoredKwCount} restored)`);
                  } else {
                    clearedParts.push(
                      `Keyword (${currentKeywords.length - survivingKw.length} removed)`
                    );
                  }
                }
              } catch {
                if (!setsEqual(brandRef.current, newBrand)) return;
                keywordRef.current = [];
                setKeyword([]);
                clearedParts.push("Keyword");
              }
            } else {
              // No brand and no ASIN → keyword must be cleared (requires a parent)
              keywordRef.current = [];
              setKeyword([]);
              clearedParts.push("Keyword");
            }
          }

          if (clearedParts.length > 0) {
            showFilterResetToast(
              `Brand changed — ${clearedParts.join(" and ")} filter${clearedParts.length > 1 ? "s have" : " has"} been reset`
            );
          }
        })();
      }
    },
    [showFilterResetToast]
  );

  const handleAsinChange = useCallback(
    (newAsin: string[]) => {
      const oldAsin = asinRef.current;
      asinRef.current = newAsin;
      asinIntentRef.current = newAsin; // User's explicit choice = new intent
      setAsin(newAsin);

      // Removed `oldAsin.length > 0` guard so keywords are also
      // re-validated when going from 0→N ASINs
      if (!setsEqual(newAsin, oldAsin)) {
        // Invalidate brand cache so next open re-fetches with new ASIN context
        optionsFingerprintRef.current.brand = null;
        setBrandOptions([]);

        optionsFingerprintRef.current.keyword = null;
        setKeywordOptions([]);

        const currentKeywords = [...keywordIntentRef.current];
        if (currentKeywords.length === 0) return;

        void (async () => {
          const currentBrand = brandRef.current;
          if (newAsin.length > 0 || currentBrand.length > 0) {
            try {
              const result = await fetchMarketWatchFilterOptions("keyword", {
                brand: currentBrand.length > 0 ? currentBrand : undefined,
                target_asin: newAsin.length > 0 ? newAsin : undefined,
              });
              // Staleness guard
              if (!setsEqual(asinRef.current, newAsin)) return;

              const validKeywords = new Set(result.values.map((v) => v.toLowerCase()));
              const surviving = currentKeywords.filter((k) => validKeywords.has(k.toLowerCase()));

              if (surviving.length < currentKeywords.length) {
                keywordRef.current = surviving;
                setKeyword(surviving);

                if (surviving.length === 0) {
                  showFilterResetToast("ASIN changed — Keyword filter has been reset");
                } else {
                  showFilterResetToast(
                    `ASIN changed — ${currentKeywords.length - surviving.length} keyword(s) removed`
                  );
                }
              }
            } catch {
              if (!setsEqual(asinRef.current, newAsin)) return;
              keywordRef.current = [];
              setKeyword([]);
              showFilterResetToast("ASIN changed — Keyword filter has been reset");
            }
          } else {
            // No ASIN and no brand → keyword must be cleared
            keywordRef.current = [];
            setKeyword([]);
            showFilterResetToast("ASIN changed — Keyword filter has been reset");
          }
        })();
      }
    },
    [showFilterResetToast]
  );

  const handleKeywordChange = useCallback((newKeyword: string[]) => {
    keywordRef.current = newKeyword;
    keywordIntentRef.current = newKeyword; // User's explicit choice = new intent
    setKeyword(newKeyword);
  }, []);

  // Keyword filter is disabled until at least one brand or ASIN is selected
  const keywordDisabled = brand.length === 0 && asin.length === 0;

  // Progress tracking for analysis
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [progressPhase, setProgressPhase] = useState("");

  // Stale-analysis confirmation state
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const staleAnalysisContextRef = useRef<{
    timestamp: string;
    filters: MarketWatchFilterState;
  } | null>(null);

  // Polling ref for cleanup
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tracks what sibling-filter state each dropdown was last loaded with.
  // null = never loaded (dropdown not yet opened).
  const optionsFingerprintRef = useRef<{
    brand: string | null;
    target_asin: string | null;
    keyword: string | null;
  }>({ brand: null, target_asin: null, keyword: null });

  // Refs tracking current filter values — always up to date, avoids stale closures
  const brandRef = useRef<string[]>([]);
  const asinRef = useRef<string[]>([]);
  const keywordRef = useRef<string[]>([]);

  // Intent refs: remember the user's explicit selection (before cascade pruning)
  // so that adding a parent filter can restore previously-pruned children.
  const asinIntentRef = useRef<string[]>([]);
  const keywordIntentRef = useRef<string[]>([]);

  // State for lazy-fetched full brand list (for exclusion UI)
  // Must be state (not ref) so BrandFilter re-renders when the list arrives
  const [allBrands, setAllBrands] = useState<string[]>([]);
  const allBrandsFetchedRef = useRef(false);

  // Snapshot refs: capture selection state when a dropdown opens,
  // so the close handler can detect whether the user actually changed anything.
  const brandSnapshotRef = useRef<string[]>([]);
  const asinSnapshotRef = useRef<string[]>([]);
  const keywordSnapshotRef = useRef<string[]>([]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  // Lazy fetch all brands for the exclusion UI — triggers once when first ASIN is selected
  useEffect(() => {
    if (asin.length > 0 && !allBrandsFetchedRef.current) {
      allBrandsFetchedRef.current = true;
      fetchMarketWatchFilterOptions("brand", {})
        .then((result) => {
          setAllBrands(result.values);
        })
        .catch((err) => {
          console.warn("Failed to fetch all brands for exclusion:", err);
        });
    }
  }, [asin]);

  // Reset allBrands when ASINs are fully cleared
  useEffect(() => {
    if (asin.length === 0 && allBrandsFetchedRef.current) {
      allBrandsFetchedRef.current = false;
      setAllBrands([]);
    }
  }, [asin]);

  // Listen for stale-analysis detection from ribbon refresh (CustomEvent + storage backup)
  useEffect(() => {
    const handleStaleDetected = async () => {
      try {
        const raw = await OfficeRuntime.storage.getItem(MW_STALE_ANALYSIS_STORAGE_KEY);
        if (raw) {
          const ctx = JSON.parse(raw);
          staleAnalysisContextRef.current = ctx;
          setShowRefreshConfirm(true);
        }
      } catch (err) {
        console.warn("Failed to read stale-analysis context:", err);
      }
    };

    window.addEventListener(MW_STALE_ANALYSIS_EVENT, handleStaleDetected);

    // On mount, check if storage flag was set while taskpane was closed
    handleStaleDetected();

    return () => {
      window.removeEventListener(MW_STALE_ANALYSIS_EVENT, handleStaleDetected);
    };
  }, []);

  // Restore saved filters: try active sheet's custom property first, then global storage
  const loadFiltersFromSheet = useCallback(async () => {
    try {
      const sheetsExist = await anyMarketWatchSheetsExist();
      if (!sheetsExist) {
        console.log("No Market Watch sheets found; clearing persisted filters");
        await clearMarketWatchFilters(MARKET_WATCH_SHEET);
        brandRef.current = [];
        asinRef.current = [];
        keywordRef.current = [];
        asinIntentRef.current = [];
        keywordIntentRef.current = [];
        setBrand([]);
        setAsin([]);
        setKeyword([]);
        return;
      }

      // Try per-sheet custom property first (active sheet)
      const sheetFilters = await loadFiltersFromActiveMWSheet();
      if (
        sheetFilters &&
        (sheetFilters.asin.length > 0 ||
          sheetFilters.keyword.length > 0 ||
          sheetFilters.brand.length > 0)
      ) {
        brandRef.current = sheetFilters.brand || [];
        asinRef.current = sheetFilters.asin;
        asinIntentRef.current = sheetFilters.asin;
        setBrand(sheetFilters.brand || []);
        setAsin(sheetFilters.asin);
        // Only restore keywords if brand or ASIN is present (enforce dependency)
        const hasBrandOrAsin = sheetFilters.brand?.length > 0 || sheetFilters.asin.length > 0;
        const restoredKeywords = hasBrandOrAsin ? sheetFilters.keyword : [];
        keywordRef.current = restoredKeywords;
        keywordIntentRef.current = restoredKeywords;
        setKeyword(restoredKeywords);
        setExcludedBrands(sheetFilters.excludedBrands || []);
        setIsExcludeSameBrand(sheetFilters.isExcludeSameBrand ?? false);
        setIsExcludeSpreetailAsin(sheetFilters.isExcludeSpreetailAsin ?? false);
        setIsEnabledSeasonality(sheetFilters.isEnabledSeasonality ?? false);
        setPriceClusterMode(sheetFilters.priceClusterMode ?? PRICE_CLUSTER_MODES.GLOBAL);
        setPriceVariancePct(sheetFilters.priceVariancePct ?? 10);
        setVarianceGrowthFactor(sheetFilters.varianceGrowthFactor ?? null);
        console.log("Restored MW filters from active sheet:", sheetFilters);
        sheetRestoredRef.current = true;
        return;
      }

      // Fallback: global storage (for backward compat / non-MW active sheet)
      console.log(`Falling back to global storage key: "${MARKET_WATCH_SHEET}"`);
      const saved = await loadMarketWatchFilters(MARKET_WATCH_SHEET);
      if (saved.asin.length > 0 || saved.keyword.length > 0 || saved.brand.length > 0) {
        brandRef.current = saved.brand || [];
        asinRef.current = saved.asin;
        asinIntentRef.current = saved.asin;
        setBrand(saved.brand || []);
        setAsin(saved.asin);
        // Only restore keywords if brand or ASIN is present (enforce dependency)
        const hasBrandOrAsin = saved.brand?.length > 0 || saved.asin.length > 0;
        const restoredKw = hasBrandOrAsin ? saved.keyword : [];
        keywordRef.current = restoredKw;
        keywordIntentRef.current = restoredKw;
        setKeyword(restoredKw);
        setExcludedBrands(saved.excludedBrands || []);
        setIsExcludeSameBrand(saved.isExcludeSameBrand ?? false);
        setIsExcludeSpreetailAsin(saved.isExcludeSpreetailAsin ?? false);
        setIsEnabledSeasonality(saved.isEnabledSeasonality ?? false);
        setPriceClusterMode(saved.priceClusterMode ?? PRICE_CLUSTER_MODES.GLOBAL);
        setPriceVariancePct(saved.priceVariancePct ?? 10);
        setVarianceGrowthFactor(saved.varianceGrowthFactor ?? null);
        sheetRestoredRef.current = true;
        console.log("Restored MW filters from global storage:", saved);
      } else {
        console.log("No saved filters found for Market Watch");
      }
    } catch (err) {
      console.warn("Could not restore market watch filters:", err);
    }
  }, []);

  // ── Navigation-safe filter persistence (separate from sheet-based persistence) ──
  // ── Temp filter persistence: survives Config Settings round-trip only ──
  const MW_NAV_FILTERS_KEY = STORAGE_KEYS.MW_NAV_FILTERS;

  // On mount: try sheet-based restore first, then fall back to temp nav storage
  useEffect(() => {
    (async () => {
      await loadFiltersFromSheet();
      if (brandRef.current.length > 0 || asinRef.current.length > 0 || keywordRef.current.length > 0) {
        OfficeRuntime.storage.removeItem(MW_NAV_FILTERS_KEY);
        return;
      }
      try {
        const raw = await OfficeRuntime.storage.getItem(MW_NAV_FILTERS_KEY);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (!saved.brand?.length && !saved.asin?.length && !saved.keyword?.length) return;
        brandRef.current = saved.brand || [];
        asinRef.current = saved.asin || [];
        asinIntentRef.current = saved.asin || [];
        keywordRef.current = saved.keyword || [];
        keywordIntentRef.current = saved.keyword || [];
        setBrand(saved.brand || []);
        setAsin(saved.asin || []);
        setKeyword(saved.keyword || []);
        setExcludedBrands(saved.excludedBrands || []);
        setIsExcludeSameBrand(saved.isExcludeSameBrand ?? false);
        setIsExcludeSpreetailAsin(saved.isExcludeSpreetailAsin ?? false);
        setIsEnabledSeasonality(saved.isEnabledSeasonality ?? false);
        setPriceClusterMode(saved.priceClusterMode ?? PRICE_CLUSTER_MODES.GLOBAL);
        setPriceVariancePct(saved.priceVariancePct ?? 10);
        setVarianceGrowthFactor(saved.varianceGrowthFactor ?? null);
        sheetRestoredRef.current = true;
      } catch {
        // ignore parse errors
      }
      // Clean up after restoring — single-use
      OfficeRuntime.storage.removeItem(MW_NAV_FILTERS_KEY);
    })();
  }, [loadFiltersFromSheet]);

  // Fetch user's MongoDB config defaults for cluster settings
  useEffect(() => {
    if (configLoadedRef.current) return undefined;
    let cancelled = false;

    const loadConfigDefaults = async () => {
      setClusterSettingsLoading(true);
      try {
        const config = await fetchFeasibilityWeights();
        if (cancelled) return;
        configLoadedRef.current = true;

        const cs = config.dynamic_cluster_settings;
        console.log("[ClusterSettings] Config fetched from MongoDB:", {
          price_variance_pct: cs?.price_variance_pct,
          variance_growth_factor: cs?.variance_growth_factor,
          sheetAlreadyRestored: sheetRestoredRef.current,
          willApply: !sheetRestoredRef.current && !!cs,
        });
        // Only apply config defaults if no per-sheet restore has happened
        if (!sheetRestoredRef.current && cs) {
          setPriceVariancePct(cs.price_variance_pct ?? 10);
          setVarianceGrowthFactor(cs.variance_growth_factor ?? 1.0);
        }
      } catch (err) {
        console.warn("Could not load cluster config defaults:", err);
        // Fall back to system defaults: 10% variance, 1.0 growth factor
        if (!cancelled && !sheetRestoredRef.current) {
          setVarianceGrowthFactor(1.0);
        }
      } finally {
        if (!cancelled) setClusterSettingsLoading(false);
      }
    };

    loadConfigDefaults();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save filters to temp storage and navigate to Config Settings
  const handleNavigateToSettings = useCallback(() => {
    const hasFilters = brandRef.current.length > 0 || asinRef.current.length > 0 || keywordRef.current.length > 0;
    if (hasFilters) {
      OfficeRuntime.storage.setItem(
        MW_NAV_FILTERS_KEY,
        JSON.stringify({
          brand: brandRef.current,
          asin: asinRef.current,
          keyword: keywordRef.current,
          excludedBrands,
          isExcludeSameBrand,
          isExcludeSpreetailAsin,
          priceClusterMode,
          priceVariancePct,
          varianceGrowthFactor,
        })
      );
    }
    history.push("/configuration-settings");
  }, [excludedBrands, isExcludeSameBrand, isExcludeSpreetailAsin, priceClusterMode, priceVariancePct, varianceGrowthFactor, history]);

  // ── Fingerprint helper: serializes sibling selections for cache comparison ──
  const getFingerprint = useCallback(
    (field: "brand" | "target_asin" | "keyword") => {
      const parts: Record<string, string[]> = {};
      if (field !== "brand" && brand.length > 0) parts.brand = brand;
      if (field !== "target_asin" && asin.length > 0) parts.target_asin = asin;
      if (field !== "keyword" && keyword.length > 0) parts.keyword = keyword;
      return JSON.stringify(parts);
    },
    [brand, asin, keyword]
  );

  // ── Lazy cascading filter fetchers (with fingerprint cache) ───
  const fetchBrandOptions = useCallback(async () => {
    const fp = getFingerprint("brand");
    if (optionsFingerprintRef.current.brand === fp) return;

    setBrandLoading(true);

    try {
      const result = await fetchMarketWatchFilterOptions("brand", {
        target_asin: asin.length > 0 ? asin : undefined,
        keyword: keyword.length > 0 ? keyword : undefined,
      });
      setBrandOptions(result.values);
      optionsFingerprintRef.current.brand = fp;
    } catch (err) {
      console.error("Failed to fetch brand options:", err);
    } finally {
      setBrandLoading(false);
    }
  }, [asin, keyword, getFingerprint]);

  const fetchAsinOptions = useCallback(async () => {
    const fp = getFingerprint("target_asin");
    if (optionsFingerprintRef.current.target_asin === fp) return;

    setAsinLoading(true);

    try {
      const result = await fetchMarketWatchFilterOptions("target_asin", {
        brand: brand.length > 0 ? brand : undefined,
        keyword: keyword.length > 0 ? keyword : undefined,
      });
      setAsinOptions(result.values);
      optionsFingerprintRef.current.target_asin = fp;
    } catch (err) {
      console.error("Failed to fetch ASIN options:", err);
    } finally {
      setAsinLoading(false);
    }
  }, [brand, keyword, getFingerprint]);

  const fetchKeywordOptions = useCallback(async () => {
    const fp = getFingerprint("keyword");
    if (optionsFingerprintRef.current.keyword === fp) return;

    setKeywordLoading(true);

    try {
      const result = await fetchMarketWatchFilterOptions("keyword", {
        brand: brand.length > 0 ? brand : undefined,
        target_asin: asin.length > 0 ? asin : undefined,
      });
      setKeywordOptions(result.values);
      optionsFingerprintRef.current.keyword = fp;
    } catch (err) {
      console.error("Failed to fetch keyword options:", err);
    } finally {
      setKeywordLoading(false);
    }
  }, [brand, asin, getFingerprint]);

  // ── Open handlers: capture snapshot before fetching ──
  const handleBrandOpen = useCallback(() => {
    brandSnapshotRef.current = [...brandRef.current];
    fetchBrandOptions();
  }, [fetchBrandOptions]);

  const handleAsinOpen = useCallback(() => {
    asinSnapshotRef.current = [...asinRef.current];
    fetchAsinOptions();
  }, [fetchAsinOptions]);

  const handleKeywordOpen = useCallback(() => {
    keywordSnapshotRef.current = [...keywordRef.current];
    fetchKeywordOptions();
  }, [fetchKeywordOptions]);

  // ── Close handlers: only invalidate DOWNSTREAM fingerprints, only if selection changed ──
  // No refetching on close — the fingerprint check on next open handles it.
  const handleBrandClose = useCallback(() => {
    if (!setsEqual(brandRef.current, brandSnapshotRef.current)) {
      // Brand changed → downstream (ASIN, Keyword) need fresh options on next open
      optionsFingerprintRef.current.target_asin = null;
      optionsFingerprintRef.current.keyword = null;
    }
  }, []);

  const handleAsinClose = useCallback(() => {
    if (!setsEqual(asinRef.current, asinSnapshotRef.current)) {
      // ASIN changed → only downstream (Keyword) needs fresh options
      // Brand is upstream — never invalidated by ASIN changes
      optionsFingerprintRef.current.keyword = null;
    }
  }, []);

  const handleKeywordClose = useCallback(() => {
    // Keyword is terminal — no downstream filters to invalidate
  }, []);

  // Listen for sheet activation and deletion to restore/reset filters
  useEffect(() => {
    let activatedHandler: OfficeExtension.EventHandlerResult<Excel.WorksheetActivatedEventArgs> | null =
      null;
    let deletedHandler: OfficeExtension.EventHandlerResult<Excel.WorksheetDeletedEventArgs> | null =
      null;

    Excel.run(async (context) => {
      activatedHandler = context.workbook.worksheets.onActivated.add(async () => {
        await loadFiltersFromSheet();
      });
      deletedHandler = context.workbook.worksheets.onDeleted.add(async () => {
        await loadFiltersFromSheet();
      });
      await context.sync();
    }).catch((err) => console.warn("Failed to register sheet event handlers:", err));

    return () => {
      if (activatedHandler || deletedHandler) {
        Excel.run(async (context) => {
          if (activatedHandler) activatedHandler.remove();
          if (deletedHandler) deletedHandler.remove();
          await context.sync();
        }).catch(() => {});
      }
    };
  }, [loadFiltersFromSheet]);

  const handleApplyFilter = async (overrideFilters?: MarketWatchFilterState) => {
    const filters = overrideFilters ?? {
      asin,
      keyword,
      brand,
      excludedBrands,
      isExcludeSameBrand,
      isExcludeSpreetailAsin,
      isEnabledSeasonality,
      priceClusterMode,
      priceVariancePct,
      varianceGrowthFactor,
    };
    console.log("[ClusterSettings] Execute clicked with:", {
      mode: filters.priceClusterMode,
      variance_pct: filters.priceVariancePct,
      growth_factor: filters.varianceGrowthFactor,
      isManual: filters.priceClusterMode === PRICE_CLUSTER_MODES.DYNAMIC,
    });
    setLoading(true);
    setError(null);
    setProgressPercent(0);
    setProgressMessage("Fetching data...");
    setProgressPhase("data");

    const abortSignal = { cancelled: false };

    try {
      await executeMarketWatchPipeline(
        filters,
        (progress) => {
          setProgressPercent(progress.percent);
          setProgressMessage(progress.message);
          setProgressPhase(progress.phase);
        },
        abortSignal
      );
      setProgressPercent(100);
      setProgressMessage("Complete!");
    } catch (err: any) {
      console.error("Market Watch apply error:", err);
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false);
      setProgressPercent(0);
      setProgressMessage("");
      setProgressPhase("");
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    }
  };

  const handleClearFilters = async () => {
    brandRef.current = [];
    asinRef.current = [];
    keywordRef.current = [];
    asinIntentRef.current = [];
    keywordIntentRef.current = [];
    setBrand([]);
    setAsin([]);
    setKeyword([]);
    setExcludedBrands([]);
    setIsExcludeSameBrand(false);
    setIsEnabledSeasonality(false);
    setPriceClusterMode(PRICE_CLUSTER_MODES.GLOBAL);
    setPriceVariancePct(10);
    setVarianceGrowthFactor(null);
    setBrandOptions([]);
    setAsinOptions([]);
    setKeywordOptions([]);
    setError(null);
    optionsFingerprintRef.current = { brand: null, target_asin: null, keyword: null };
    await clearMarketWatchFilters(MARKET_WATCH_SHEET);
    OfficeRuntime.storage.removeItem(MW_NAV_FILTERS_KEY);

    // Also clear custom property on the active MW sheet
    try {
      await Excel.run(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        activeSheet.load("name");
        await context.sync();

        if (activeSheet.name.startsWith("MW ")) {
          const prop = activeSheet.customProperties.getItemOrNullObject("mw_filters");
          prop.load("isNullObject");
          await context.sync();
          if (!prop.isNullObject) {
            prop.delete();
            await context.sync();
          }
        }
      });
    } catch (err) {
      console.warn("Could not clear MW filters from active sheet:", err);
    }
  };

  // ── Stale-analysis confirmation handlers ──────────────────────
  const clearStaleAnalysisState = async () => {
    setShowRefreshConfirm(false);
    staleAnalysisContextRef.current = null;
    try {
      await OfficeRuntime.storage.removeItem(MW_STALE_ANALYSIS_STORAGE_KEY);
    } catch (_) {}
  };

  const handleRerunAnalysis = async () => {
    const ctx = staleAnalysisContextRef.current;
    await clearStaleAnalysisState();
    if (!ctx) return;

    setLoading(true);
    setError(null);
    setProgressPercent(0);
    setProgressMessage("Re-analyzing data...");
    setProgressPhase("analyze");

    try {
      await rerunMarketWatchAnalysis(ctx.timestamp, ctx.filters, (progress) => {
        setProgressPercent(progress.percent);
        setProgressMessage(progress.message);
        setProgressPhase(progress.phase);
      });
      setProgressPercent(100);
      setProgressMessage("Complete!");
    } catch (err: any) {
      console.error("Market Watch re-run error:", err);
      setError(err.message || "Failed to re-run analysis");
    } finally {
      setLoading(false);
      setProgressPercent(0);
      setProgressMessage("");
      setProgressPhase("");
    }
  };

  const handleDismissRefreshConfirm = async () => {
    await clearStaleAnalysisState();
  };

  // Get phase-specific step message for Market Watch
  const getPhaseMessage = (phase: string): string => {
    switch (phase) {
      case "data":
        return "Fetching filtered data...";
      case "analyze":
        return "Starting analysis execution...";
      case "analyzing":
        return "Processing market data...";
      case "sheets":
        return "Creating Excel sheets with KPIs & charts...";
      default:
        return "Processing...";
    }
  };

  // Main filter UI with progress overlay
  return (
    <>
      {/* Stale-analysis confirmation overlay */}
      <MarketWatchRefreshConfirm
        isOpen={showRefreshConfirm}
        onRerunAnalysis={handleRerunAnalysis}
        onDismiss={handleDismissRefreshConfirm}
      />

      {/* Progress Sheet Overlay */}
      <ProgressSheet
        isOpen={loading}
        progress={progressPercent}
        title="Analyzing Market Data"
        subtitle={progressMessage || "Please wait..."}
        stepMessage={getPhaseMessage(progressPhase)}
      />

      <MarketWatchFilterView
        brand={brand}
        setBrand={handleBrandChange}
        asin={asin}
        setAsin={handleAsinChange}
        keyword={keyword}
        setKeyword={handleKeywordChange}
        brandOptions={brandOptions}
        asinOptions={asinOptions}
        keywordOptions={keywordOptions}
        brandLoading={brandLoading}
        asinLoading={asinLoading}
        keywordLoading={keywordLoading}
        loading={loading}
        error={error}
        onApplyFilter={() => handleApplyFilter()}
        onClearFilters={handleClearFilters}
        onBrandOpen={handleBrandOpen}
        onAsinOpen={handleAsinOpen}
        onKeywordOpen={handleKeywordOpen}
        onBrandClose={handleBrandClose}
        onAsinClose={handleAsinClose}
        onKeywordClose={handleKeywordClose}
        keywordDisabled={keywordDisabled}
        excludedBrands={excludedBrands}
        onExcludedChange={setExcludedBrands}
        isExcludeSameBrand={isExcludeSameBrand}
        onExcludeSameBrandChange={setIsExcludeSameBrand}
        isExcludeSpreetailAsin={isExcludeSpreetailAsin}
        onExcludeSpreetailAsinChange={setIsExcludeSpreetailAsin}
        allBrands={allBrands}
        hasSelectedAsins={brand.length > 0 || asin.length > 0 || keyword.length > 0}
        priceClusterMode={priceClusterMode}
        onPriceClusterModeChange={setPriceClusterMode}
        priceVariancePct={priceVariancePct}
        varianceGrowthFactor={varianceGrowthFactor}
        onPriceVariancePctChange={setPriceVariancePct}
        onVarianceGrowthFactorChange={setVarianceGrowthFactor}
        clusterSettingsLoading={clusterSettingsLoading}
        onNavigateToSettings={handleNavigateToSettings}
        isEnabledSeasonality={isEnabledSeasonality}
        onEnabledSeasonalityChange={setIsEnabledSeasonality}
      />

      {/* Toast for cascading filter resets */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="success"
          icon={false}
          sx={{
            width: "100%",
            fontSize: "12px",
            backgroundColor: "rgba(6, 142, 117, 0.9)",
            backdropFilter: "blur(8px)",
            color: "#fff",
            borderRadius: "8px",
            "& .MuiAlert-action .MuiSvgIcon-root": { color: "#fff" },
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MarketWatchRoute;
